import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion, type Variants } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { LayoutDashboard } from 'lucide-react';
import { HeroBackground, type HeroRunSignal } from './HeroBackground';
import { GuestTasteWidget } from './GuestTasteWidget';
import { resolveHeroVariant } from './heroVariant';
import { fetchPoolFacts, type PoolFacts } from '@/services/api/guestTasteApi';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: 'easeOut' },
  },
};

/**
 * Landing hero (project100 T1009 / D-015) — a DOORWAY, not a brochure:
 * one accurate promise plus the company-URL field above the fold. The guest
 * taste widget is the primary action; everything else is secondary.
 */
/**
 * " · last changed 3 days ago", or NOTHING when the backend could not say (T1039).
 *
 * The phrase is derived from the pool's own newest `entity_update_time`, so it is a fact about the
 * record and not a claim about our schedule — and a missing or unparseable timestamp renders no
 * phrase at all rather than a vaguer one, because a softer sentence about an unknown date is still a
 * sentence nobody could back.
 */
function poolChangedPhrase(iso: string | null): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const hours = Math.floor((Date.now() - then) / 3_600_000);
  if (hours < 1) return ' · last changed this hour';
  if (hours < 24) return ` · last changed ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return ` · last changed ${days} day${days === 1 ? '' : 's'} ago`;
}

export const HeroSection = () => {
  const { user } = usePublicAuth();
  /** T1010 — `full` unless something explicitly asks for the minimal variant (see heroVariant.ts). */
  const variant = resolveHeroVariant();
  /**
   * D-031 option B — the backdrop IS the run. The hero holds the signal and hands it to the canvas;
   * the widget owns the run itself, so the canvas never learns anything the widget does not know.
   */
  const [run, setRun] = useState<HeroRunSignal | null>(null);
  const onRunSignal = useCallback((signal: HeroRunSignal | null) => setRun(signal), []);
  /**
   * THE IDLE STATE SAYS WHAT THE MACHINE HOLDS (D-031 option B's "real last-checked line"). Fetched
   * once, after mount, off the critical path, and only ever rendered when the backend answered with a
   * real number — see fetchPoolFacts for why silence is the failure mode we chose.
   */
  const [poolFacts, setPoolFacts] = useState<PoolFacts | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchPoolFacts().then((facts) => {
      if (!cancelled && facts) setPoolFacts(facts);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * T1010 — THE MINIMAL HERO, DORMANT. Same doorway, same backdrop carrying the same run, same
   * real-state line; the badge, the headline and the secondary links are gone. Reached only when
   * something asks for it; the branch below is the hero that ships today.
   *
   * The copy is NOT new — it is the promise paragraph this page already makes (T1009 / D-015),
   * verbatim. A variant that invented its own wording would be testing the words as well as the
   * layout, and neither result would be attributable to one of them.
   */
  if (variant === 'minimal') {
    return (
      <section className="relative overflow-hidden">
        <HeroBackground run={run} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div
            className="text-center space-y-6 md:space-y-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Drop your company URL — see 5 investor outreach emails drafted for you, free. Real
              drafts from your real site. Send them when you&apos;re ready.
            </motion.p>

            <motion.div variants={itemVariants} className="pt-2">
              <GuestTasteWidget onRunSignal={onRunSignal} />
            </motion.div>

            {run === null && poolFacts && poolFacts.investors > 0 ? (
              <motion.p
                variants={itemVariants}
                className="text-sm text-slate-500 dark:text-slate-400"
              >
                The engine is holding{" "}
                <span className="font-medium text-slate-700 dark:text-slate-200">
                  {poolFacts.investors.toLocaleString()}
                </span>{" "}
                investor records{poolChangedPhrase(poolFacts.changed_at)}.
              </motion.p>
            ) : null}
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden">
      {/* Signature engine motif behind the hero (D-009), carrying the run itself (D-031 option B) */}
      <HeroBackground run={run} />

      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="text-center space-y-6 md:space-y-8">
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 rounded-full text-sm font-medium border border-cyan-200 dark:border-cyan-800"
            variants={itemVariants}
          >
            <span className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></span>
            Early Access • Backed by Public Benchmark Data
          </motion.div>

          {/* Headline */}
          <motion.h1
            className="text-display font-bold text-slate-900 dark:text-white leading-tight max-w-5xl mx-auto"
            variants={itemVariants}
          >
            Your Fundraising Department, in AI.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-teal-500">
              You Keep the Calls.
            </span>
          </motion.h1>

          {/* The one accurate promise */}
          <motion.p
            className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            Drop your company URL — see 5 investor outreach emails drafted for you, free. Real
            drafts from your real site. Send them when you&apos;re ready.
          </motion.p>

          {/* The doorway: URL in, drafts out */}
          <motion.div variants={itemVariants} className="pt-2">
            <GuestTasteWidget onRunSignal={onRunSignal} />
          </motion.div>

          {/* WHAT THE MACHINE IS HOLDING, WHILE IT IS DOING NOTHING (D-031 option B). Retired the
              moment a run starts: then the field and the panel carry the work, and a second number
              beside them would be one fact in two places. */}
          {run === null && poolFacts && poolFacts.investors > 0 ? (
            <motion.p
              variants={itemVariants}
              className="text-sm text-slate-500 dark:text-slate-400"
            >
              The engine is holding{" "}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {poolFacts.investors.toLocaleString()}
              </span>{" "}
              investor records{poolChangedPhrase(poolFacts.changed_at)}.
            </motion.p>
          ) : null}

          {/* Secondary paths */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2"
            variants={itemVariants}
          >
            {user && (
              <Link to="/entities">
                <Button variant="outline" className="gap-2">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Button>
              </Link>
            )}
            <Link to="/about">
              <Button variant="ghost" className="px-6">
                See How It Works
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};
